import React, { useState, useEffect, useCallback } from 'react';

import logger from '../utils/logger.js';

import Toast from './Toast';

/**
 * Overlays/panels whose footers sit in the same bottom band as this banner.
 * Prefer suppress-on-open over a z-index race (ISS-690).
 * PrivacyBanner itself is role="dialog" without aria-modal, so it is not matched.
 */
const BLOCKING_OVERLAY_SELECTOR = [
  '.ds-add-event-panel',
  '.ds-notifications-panel',
  '.ds-modal-overlay',
  '.ds-panel-overlay',
  '.fd-modal-overlay',
  '.fd-panel-overlay',
  '.dw-modal-overlay',
  '.dw-confirm-overlay',
  '.au-modal-overlay',
  '.au-panel-overlay',
  '.ar-modal-overlay',
  '.ar-panel-overlay',
  '.dp-panel-overlay',
  '.al-panel-overlay',
  '.modal-overlay-apple',
  '.explain-modal-overlay',
  '.settings-drawer-overlay',
  '.worker-profile-overlay',
  '.command-bar-overlay',
  '.pm-create-modal-root',
  '.pm-detail-panel',
  '.bolt-panel-overlay',
  '.stripe-sidebar-overlay.show',
  '[aria-modal="true"]',
].join(',');

function hasBlockingOverlay() {
  return Boolean(document.querySelector(BLOCKING_OVERLAY_SELECTOR));
}

const PrivacyBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [acceptedConsent, setAcceptedConsent] = useState(false);
  const [toast, setToast] = useState(null);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // Suppress unused var warning - keeping for future use
  logger.info('Consent status:', { context: acceptedConsent });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('privacy-consent');
    const consentData = consent ? JSON.parse(consent) : null;

    if (!consentData || !consentData.accepted) {
      setShowBanner(true);
    } else {
      setAcceptedConsent(true);
    }
  }, []);

  useEffect(() => {
    const syncOverlayState = () => {
      setOverlayOpen(hasBlockingOverlay());
    };
    syncOverlayState();
    const observer = new MutationObserver(syncOverlayState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-modal'],
    });
    return () => observer.disconnect();
  }, []);

  const acceptConsent = useCallback(() => {
    const consentData = {
      accepted: true,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    localStorage.setItem('privacy-consent', JSON.stringify(consentData));
    setShowBanner(false);
    setAcceptedConsent(true);

    // Track consent for compliance
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'privacy_consent_accepted',
        consent_version: '1.0',
        timestamp: consentData.timestamp,
      });
    }
  }, []);

  const declineConsent = useCallback(() => {
    // For GDPR compliance, allow users to decline
    const consentData = {
      accepted: false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    localStorage.setItem('privacy-consent', JSON.stringify(consentData));

    // Redirect to basic version or show limited functionality message
    setToast({
      message: 'You have declined consent. Some features may be limited.',
      type: 'info',
    });
    setShowBanner(false);
  }, []);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  if (!showBanner) {
    return (
      <>{toast && <Toast message={toast.message} type={toast.type} onClose={handleToastClose} />}</>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '20px',
        zIndex: 9999,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        pointerEvents: overlayOpen ? 'none' : 'auto',
        visibility: overlayOpen ? 'hidden' : 'visible',
      }}
      role="dialog"
      aria-labelledby="privacy-banner-title"
      aria-hidden={overlayOpen}
      data-testid="privacy-banner"
      data-overlay-suppressed={overlayOpen ? 'true' : 'false'}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h3 id="privacy-banner-title" style={{ margin: '0 0 10px 0', fontSize: '18px' }}>
          Privacy & Cookie Notice
        </h3>
        <p style={{ margin: '0 0 15px 0', lineHeight: '1.5' }}>
          We use cookies and similar technologies to enhance your experience, analyze site usage,
          and assist with our marketing efforts. By clicking &quot;Accept All&quot;, you consent to
          our use of cookies. You can manage your preferences at any time.
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={acceptConsent}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            data-testid="accept-consent"
          >
            Accept All
          </button>
          <button
            onClick={declineConsent}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid white',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            data-testid="decline-consent"
          >
            Decline
          </button>
          <a
            href="/privacy"
            style={{
              color: '#3498db',
              textDecoration: 'underline',
              padding: '10px 0',
              fontSize: '14px',
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={handleToastClose} />}
    </div>
  );
};

export default PrivacyBanner;
